import axios from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authLogin } from '@/api/endpoints';
import { useToast } from '@/lib/ui';
import { Device } from '@/lib/device';

export default function Login({ device }: { device: Device }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRecover, setShowRecover] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const m = useMutation({
    mutationFn: (input: { email: string; password: string }) => authLogin(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      nav('/dashboard');
    },
    onError: (err: unknown) => {
      // 429 + 403 (MustChangePassword) handled by global session handler
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        toast.push('Email ou mot de passe invalide', 'error');
      } else if (axios.isAxiosError(err) && err.response?.status === 400) {
        const msg = (err.response.data as { message?: string })?.message ?? 'Champ invalide';
        toast.push(`Validation : ${msg}`, 'error');
      } else if (!axios.isAxiosError(err) || (err.response?.status !== 429 && err.response?.status !== 403)) {
        const code = axios.isAxiosError(err) ? err.code : undefined;
        if (code !== 'ERR_CANCELED') toast.push('Erreur de connexion', 'error');
      }
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    m.mutate({ email, password });
  }

  const mobile = device !== 'desktop';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 bg-background text-foreground ${mobile ? 'pt-8' : ''}`}>
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-card border border-border rounded-xl p-6 shadow-lg space-y-5">
        <div className="flex flex-col items-center gap-2 mb-2">
          <img src="https://www.meteor-oi.re/wp-content/uploads/2024/08/MeteoR-OI_Logo.png" alt="MétéoR-OI" className="h-14" />
          <h1 className="text-xl font-bold">climato_admin</h1>
          <p className="text-xs text-muted-foreground">Connexion administrateur</p>
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1.5">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1.5">Mot de passe</label>
          <input
            id="password"
            type="password"
            required
            minLength={12}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">12 caractères minimum</p>
        </div>
        <button
          type="submit"
          disabled={m.isPending}
          className="w-full h-10 rounded-md bg-primary text-primary-foreground font-medium disabled:opacity-50"
        >
          {m.isPending ? 'Connexion…' : 'Se connecter'}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowRecover(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Mot de passe perdu ?
          </button>
        </div>
      </form>

      {showRecover && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowRecover(false)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-2xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-bold">Mot de passe perdu</h3>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-muted-foreground">
              <p>
                Aucune réinitialisation automatique. Contactez un <strong>superAdmin</strong> :
                il peut réinitialiser votre mot de passe depuis l’écran « Utilisateurs ».
              </p>
              <p>
                Si vous êtes le seul superAdmin et bloqué, utilisez le script de secours
                côté serveur :
              </p>
              <pre className="bg-muted rounded-md p-2 text-[11px] font-mono whitespace-pre-wrap text-foreground">
RESET_EMAIL=vous@exemple
RESET_PASSWORD=&apos;motDePasse12+&apos;
pnpm tsx scripts/reset-password.ts
              </pre>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end">
              <button
                type="button"
                onClick={() => setShowRecover(false)}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
